import { HourLine, PAYROLL_LINE_CODES, PayrollRun, PayrollRunEmployee } from './usePayrollRun.interface';

import { moneyFormat } from 'modules/common/display';
import { PayrollLine, PayrollRunDetail, PayrollRunItem, TeamMember } from 'services/payroll/api/generated/v1/model';

/**
 * A category has three states, not two, and the difference only shows up in an aggregate.
 *
 * - **absent** — the worker has no line of that kind. A *cell* shows nothing, because printing
 *   `$0.00` claims we know a figure; an *aggregate* adds nothing, because there is nothing to add.
 * - **indeterminate** — there are lines, but the figure cannot be settled: one carries no amount, or
 *   one cannot be attributed to a party. Both the cell and the aggregate must say so.
 * - otherwise a decimal string, two places, matching the wire's own scale.
 *
 * Collapsing the first two onto `null` is safe for a cell and wrong for an aggregate: adding the
 * parts that are present and dropping the rest prints a number short by an unknown amount, looking
 * every bit as settled as a real one. `contributionOf` and `cellValue` below are the two readings.
 */
/**
 * What a category contributes to an aggregate: `'0.00'` when absent, `null` when indeterminate.
 *
 * `party` narrows a category to one side of it. A line of that kind the provider could not attribute
 * makes *both* sides indeterminate — it belongs to one of them and we cannot say which, so neither
 * side may claim a settled figure.
 */
const contributionOf = (
    lines: PayrollLine[],
    kind: PayrollLine['kind'],
    party?: PayrollLine['party']
): string | null => {
    const ofKind = lines.filter((l) => l.kind === kind);

    if (party !== undefined && ofKind.some((l) => l.party === 'unknown')) {
        return null;
    }

    const matching = party === undefined ? ofKind : ofKind.filter((l) => l.party === party);

    if (matching.some(({ amount }) => amount === null)) {
        return null;
    }

    return matching.reduce((total, { amount }) => total + Number(amount), 0).toFixed(2);
};

/**
 * The same category as a cell reads it: absent and indeterminate are both "no figure".
 *
 * An unattributed line counts towards *both* sides being present, which is what makes an
 * unattributable tax render as a placeholder on each side rather than as an empty category on one.
 */
const cellValue = (lines: PayrollLine[], kind: PayrollLine['kind'], party?: PayrollLine['party']): string | null => {
    const ofKind = lines.filter((l) => l.kind === kind);
    const present = party === undefined ? ofKind : ofKind.filter((l) => l.party === party || l.party === 'unknown');

    return present.length === 0 ? null : contributionOf(lines, kind, party);
};

/**
 * Sums a subset of a worker's lines, or `null` when the subset is empty. Kept for the categories
 * that are not a party split and not fed into an aggregate.
 */
const sumLines = (lines: PayrollLine[], predicate: (line: PayrollLine) => boolean): string | null => {
    const matching = lines.filter(predicate);

    if (matching.length === 0) {
        return null;
    }

    if (matching.some(({ amount }) => amount === null)) {
        return null;
    }

    return matching.reduce((total, { amount }) => total + Number(amount), 0).toFixed(2);
};

/**
 * Adds contributions. Strict on purpose: every argument is already `'0.00'` where a category is
 * merely absent, so a `null` here can only mean indeterminate — and an aggregate built from a figure
 * we could not settle is not a figure either. Dropping it would understate silently.
 */
const addContributions = (...amounts: (string | null)[]): string | null =>
    amounts.some((amount) => amount === null)
        ? null
        : amounts.reduce((total, amount) => total + Number(amount), 0).toFixed(2);

const toHourLines = (lines: PayrollLine[]): HourLine[] =>
    lines.map(({ code, label, hours, amount }) => ({ code, label, hours, amount }));

/** The coded extras. Base pay arrives as `other` and must never be counted as "additional". */
const ADDITIONAL_EARNING_CODES: ReadonlySet<string> = new Set([
    PAYROLL_LINE_CODES.bonus,
    PAYROLL_LINE_CODES.cashTips,
    PAYROLL_LINE_CODES.commission,
    PAYROLL_LINE_CODES.correction,
    PAYROLL_LINE_CODES.paycheckTips,
]);

type ProjectedFromLines = Pick<
    PayrollRunEmployee,
    | 'company_paid_taxes'
    | 'company_paid_benefits'
    | 'employee_paid_taxes'
    | 'employee_benefit_contributions'
    | 'employee_deductions'
    | 'reimbursements'
    | 'hours'
    | 'time_off'
    | 'additional_earnings'
    | 'employee_taxes_and_benefits'
    | 'tax_debit'
    | 'lines'
>;

/** Every flat field that is a function of the lines alone. Shared by the first map and by an edit. */
const projectLines = (lines: PayrollLine[]): ProjectedFromLines => {
    const timeOff = lines.filter((l) => l.kind === 'time_off');

    return {
        company_paid_taxes: cellValue(lines, 'tax', 'employer'),
        company_paid_benefits: cellValue(lines, 'benefit', 'employer'),
        employee_paid_taxes: cellValue(lines, 'tax', 'employee'),
        employee_benefit_contributions: cellValue(lines, 'benefit', 'employee'),
        employee_deductions: sumLines(lines, (l) => l.kind === 'deduction'),
        reimbursements: sumLines(lines, (l) => l.kind === 'reimbursement'),
        // Summed here rather than in the column, because only here is an absent category still
        // distinguishable from one we could not settle. A column sees `null` for both.
        employee_taxes_and_benefits: addContributions(
            contributionOf(lines, 'tax', 'employee'),
            contributionOf(lines, 'benefit', 'employee')
        ),
        tax_debit: addContributions(contributionOf(lines, 'tax', 'employee'), contributionOf(lines, 'tax', 'employer')),
        hours: toHourLines(lines.filter((l) => l.kind === 'earning' && l.hours !== null)),
        time_off: timeOff.length === 0 ? null : toHourLines(timeOff),
        additional_earnings: sumLines(lines, (l) => l.kind === 'earning' && ADDITIONAL_EARNING_CODES.has(l.code)),
        lines,
    };
};

/**
 * The wire has no employer subtotal, so it is summed here rather than left blank. Gross is included
 * because this column answers "what this person costs the company", not "what was added on top".
 */
const companyPaidTotal = (grossPay: string | null, lines: PayrollLine[]) =>
    addContributions(
        grossPay,
        contributionOf(lines, 'tax', 'employer'),
        contributionOf(lines, 'benefit', 'employer'),
        contributionOf(lines, 'reimbursement')
    );

const mapItem = (item: PayrollRunItem): PayrollRunEmployee => {
    const projected = projectLines(item.lines);

    return {
        item_uuid: item.item_uuid,
        worker_uuid: item.worker_uuid,
        full_name: item.worker_name,
        job_title: item.job_title,
        is_owner: item.is_owner,
        excluded: item.excluded,
        payment_method: item.payment_method,
        gross_pay: item.gross_pay,
        net_pay: item.net_pay,
        ...projected,
        company_paid_total_amount: companyPaidTotal(item.gross_pay, item.lines),
        // Filled by `applyStandingRates` once the roster is in hand. Not optional fields: a row that
        // was never joined must read as "unknown", and the only way to be sure of that is for the
        // unjoined value to exist and be null.
        standing_gross_pay: null,
        is_anomalous: false,
        standing_rate_label: null,
        flsa_status: null,
        hourly_rate: null,
    };
};

interface LineEdit {
    lines: PayrollLine[];
    /** The provider recalculates this on a real edit; until that endpoint is wired, the form does. */
    gross_pay: string | null;
}

/**
 * A dollar. Wide enough to swallow the sub-cent drift of dividing a salary into periods — 98,000/12
 * does not land on a whole cent — and narrow enough that any change a member would care about
 * clears it. A percentage tolerance was rejected: it makes the flag less sensitive precisely for the
 * highest-paid rows, which are the expensive ones to get wrong.
 *
 * This constant exists only because the figure is derived here. If backend sends the comparison
 * instead, delete this and the whole derivation with it.
 */
const ANOMALY_TOLERANCE = 1;

/**
 * Whether this row's pay differs from the worker's standing pay by more than the tolerance. Shared
 * so a re-derivation after an edit answers the same question the first read did.
 */
const isAnomalous = (grossPay: string | null, standing: string | null, excluded: boolean): boolean =>
    standing !== null &&
    grossPay !== null &&
    !excluded &&
    Math.abs(Number(grossPay) - Number(standing)) > ANOMALY_TOLERANCE;

/**
 * The same row after a pay edit. Identity and the roster join stay; every figure that is a
 * projection of the lines is re-derived, so an edited bonus moves the Additional column and the
 * employer subtotal together rather than leaving one stale.
 */
export const withLines = (
    employee: PayrollRunEmployee,
    { lines, gross_pay: grossPay }: LineEdit
): PayrollRunEmployee => {
    const projected = projectLines(lines);

    return {
        ...employee,
        ...projected,
        gross_pay: grossPay,
        company_paid_total_amount: companyPaidTotal(grossPay, lines),
        // Re-derived, not carried: an edit that crosses the tolerance in either direction has to
        // move "Differs from usual pay" with it, or the marker describes the figure before the edit.
        is_anomalous: isAnomalous(grossPay, employee.standing_gross_pay, employee.excluded),
    };
};

/** The wire's run, as the four tabs want to read it. Pure — every figure comes from `detail`. */
export const mapRunDetail = (detail: PayrollRunDetail): PayrollRun => ({
    payroll_uuid: detail.payroll_uuid,
    pay_schedule_uuid: detail.pay_schedule?.pay_schedule_uuid ?? null,
    frequency: detail.pay_schedule?.frequency ?? null,
    start_date: detail.pay_period_start,
    end_date: detail.pay_period_end,
    check_date: detail.check_date,
    debit_date: detail.debit_date,
    payroll_deadline: detail.payroll_deadline,
    off_cycle: detail.off_cycle,
    processed: detail.processed,
    calculated_at: detail.calculated_at,
    status: detail.status,
    blockers: detail.blockers,
    can_submit: detail.can_submit,
    can_cancel: detail.can_cancel,
    can_skip: detail.can_skip,
    can_prepare: detail.can_prepare,
    // `??`: a backend from before the field omits it.
    replaced_by: detail.replaced_by ?? null,
    employee_compensations: detail.items.map(mapItem),
    totals: detail.totals,
});

/** How many times a schedule pays out in a year. Anything absent is unknown, never assumed. */
const PERIODS_PER_YEAR: Record<string, number> = {
    weekly: 52,
    biweekly: 26,
    semimonthly: 24,
    monthly: 12,
    quarterly: 4,
    annually: 1,
};

/** How many of a unit's periods make a year. `week` and `paycheck` are deliberately absent. */
const UNITS_PER_YEAR: Record<string, number> = { year: 1, month: 12 };

/**
 * Figma abbreviates the pay rate's unit under the name ("$98,000/yr"), so each unit needs a suffix
 * *and* a precision. Only an annual figure is round enough to drop cents: `$20/hr` and `$20.00/hr`
 * do not make the same claim about what is on file. `paycheck` is absent on purpose — annualising
 * it needs the schedule, and `$2,500/paycheck` is not what the design asked for.
 */
const RATE_LABELS: Record<string, { suffix: string; precision: number }> = {
    year: { suffix: '/yr', precision: 0 },
    month: { suffix: '/mo', precision: 2 },
    week: { suffix: '/wk', precision: 2 },
    hour: { suffix: '/hr', precision: 2 },
};

export const rateLabel = (rate: string | null, unit: string): string | null => {
    const format = RATE_LABELS[unit];

    if (rate === null || format === undefined) {
        return null;
    }

    const { suffix, precision } = format;

    return `${moneyFormat(Number(rate), { precision, minPrecision: precision })}${suffix}`;
};

/**
 * What this worker earns in one run of this schedule, or `null` when that cannot be derived
 * honestly.
 *
 * Only a salaried unit with a known schedule frequency converts. An hourly worker's period pay is
 * rate × hours, and hours are what the member is editing — comparing against a figure derived from
 * the very number under review says nothing. `week` and `paycheck` are unconvertible without
 * knowing how the provider aligns them to the schedule, so they stay unknown too.
 */
const standingPerPeriod = (member: TeamMember, unit: string): string | null => {
    const frequency = (member.pay_schedule?.frequency ?? '').trim().toLowerCase();
    const unitsPerYear = UNITS_PER_YEAR[unit];
    const periodsPerYear = PERIODS_PER_YEAR[frequency];

    if (member.current_salary === null || unitsPerYear === undefined || periodsPerYear === undefined) {
        return null;
    }

    return ((Number(member.current_salary) * unitsPerYear) / periodsPerYear).toFixed(2);
};

/** Folded the way the backend stores it, so a provider-spelled "Salaried Nonexempt" still matches. Blank is unknown. */
const normalizeFlsaStatus = (flsaStatus: string): string | null =>
    flsaStatus.trim().toLowerCase().replaceAll(' ', '_') || null;

/**
 * Fills each row's standing pay from the roster and flags the ones that diverge.
 *
 * Two rules protect the flag's credibility, because a marker that cries wolf gets ignored and the
 * ticket's whole point is that one wrong row must be findable among twenty:
 *
 * - **Unknown is never anomalous.** No roster match, an unconvertible unit, an unknown frequency, or
 *   a null gross all mean we have no baseline — so there is nothing to diverge from.
 * - **Excluded rows are never anomalous.** Being left out of a run is a deliberate act, and it is
 *   not the same as being paid the wrong amount.
 */
export const applyStandingRates = (run: PayrollRun, members: TeamMember[]): PayrollRun => {
    const byWorker = new Map(members.map((member) => [member.worker_uuid, member]));

    return {
        ...run,
        employee_compensations: run.employee_compensations.map((employee) => {
            const match = byWorker.get(employee.worker_uuid);

            if (match === undefined) {
                return employee;
            }

            const unit = match.payment_unit.trim().toLowerCase();
            const standing = standingPerPeriod(match, unit);

            return {
                ...employee,
                standing_gross_pay: standing,
                standing_rate_label: rateLabel(match.current_salary ?? match.hourly_rate, unit),
                hourly_rate: unit === 'hour' ? match.hourly_rate : null,
                is_anomalous: isAnomalous(employee.gross_pay, standing, employee.excluded),
                flsa_status: normalizeFlsaStatus(match.flsa_status),
            };
        }),
    };
};
